import { LISTING_TYPE_BOOK_CLASS, LISTING_TYPE_SINGLE_EVENT } from 'config/configListing';
import moment from 'moment';
import { BLOCK_BOOKING_OPTION, PAY_AS_YOU_GO_OPTION } from '../../common/config/constants';

export const generateNumberOptions = (min, max) =>
  Array.from({ length: max - min + 1 }, (_, i) => ({
    option: i + min,
    label: `${i + min}`,
  }));

export const MIN_AGE = 0;
export const MAX_AGE = 100;

const calculateWeekdaysBetween = (startDate, endDate) => {
  const start = moment(startDate);
  const end = moment(endDate);

  // Calculate total days between dates (inclusive)
  const totalDays = end.diff(start, 'days') + 1;

  // Calculate complete weeks
  const completeWeeks = Math.floor(totalDays / 7);
  const remainingDays = totalDays % 7;

  // Each complete week has 5 weekdays
  let weekdaysCount = completeWeeks * 5;

  // Check remaining days
  let current = start.clone().add(completeWeeks * 7, 'days');
  for (let i = 0; i < remainingDays; i++) {
    const dayOfWeek = current.day();
    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      weekdaysCount++;
    }
    current.add(1, 'day');
  }

  return weekdaysCount;
};

export const generateNumberOfSessionFromEndDate = (endDate, startTime, recurringOption) => {
  switch (recurringOption) {
    case 'every-day':
      return moment(endDate).diff(moment(startTime), 'days') + 1;
    case 'every-week':
      return moment(endDate).diff(moment(startTime), 'weeks') + 1;
    case 'every-weekday':
      return calculateWeekdaysBetween(startTime, endDate);
    default:
      return 1; // For 'every-weekend' or any other case, we assume 1 session
  }
};

const getMinCustomTicketPrice = (customTicket, marketplaceCurrency) => {
  const tickets = Object.values(customTicket ?? {});
  const firstTicketPrice = tickets[0]?.ticketPrice;

  if (tickets.length === 0 || !firstTicketPrice) {
    return { amount: 0, currency: marketplaceCurrency };
  }

  return tickets.slice(1).reduce((min, ticket) => {
    const price = ticket.ticketPrice;
    if (price && price.amount < min.amount) {
      return price;
    }
    return min;
  }, firstTicketPrice);
};

export const getCheapestPrice = ({
  childrenPayAsYouGo,
  childrenBlockBooking,
  childrenSingleEvent,
  adultPayAsYouGo,
  adultBlockBooking,
  adultSingleEvent,
  numberOfSessions = 1,
  pricingOptions = [],
  listingType = LISTING_TYPE_SINGLE_EVENT,
  payAsYouGoCustomTicket = {},
  blockBookingCustomTicket = {},
  singleEventCustomTicket = {},
  marketplaceCurrency,
}) => {
  // Validate numberOfSessions
  const validNumberOfSessions = Math.max(1, numberOfSessions || 1);

  if (listingType === LISTING_TYPE_SINGLE_EVENT) {
    const singleEventMinCustomTicketPrice = getMinCustomTicketPrice(
      singleEventCustomTicket,
      marketplaceCurrency
    );
    const price =
      childrenSingleEvent?.amount ||
      adultSingleEvent?.amount ||
      singleEventMinCustomTicketPrice.amount ||
      0;
    return {
      amount: price,
      currency:
        childrenSingleEvent?.currency ||
        adultSingleEvent?.currency ||
        childrenPayAsYouGo?.currency ||
        childrenBlockBooking?.currency ||
        singleEventMinCustomTicketPrice.currency,
    };
  }

  if (listingType === LISTING_TYPE_BOOK_CLASS) {
    const blockBookingMinCustomTicketPrice = getMinCustomTicketPrice(
      blockBookingCustomTicket,
      marketplaceCurrency
    );
    const payAsYouGoMinCustomTicketPrice = getMinCustomTicketPrice(
      payAsYouGoCustomTicket,
      marketplaceCurrency
    );

    // Prioritize children prices, then fall back to adult prices if needed
    if (pricingOptions.includes(PAY_AS_YOU_GO_OPTION)) {
      const childrenBlockBookingPricePerSession = childrenBlockBooking?.amount
        ? childrenBlockBooking.amount / validNumberOfSessions
        : 0;
      const adultBlockBookingPricePerSession = adultBlockBooking?.amount
        ? adultBlockBooking.amount / validNumberOfSessions
        : 0;

      return {
        amount:
          childrenPayAsYouGo?.amount ||
          childrenBlockBookingPricePerSession ||
          adultPayAsYouGo?.amount ||
          adultBlockBookingPricePerSession ||
          payAsYouGoMinCustomTicketPrice.amount ||
          blockBookingMinCustomTicketPrice.amount ||
          0,
        currency:
          childrenPayAsYouGo?.currency ||
          childrenBlockBooking?.currency ||
          adultPayAsYouGo?.currency ||
          adultBlockBooking?.currency ||
          payAsYouGoMinCustomTicketPrice.currency ||
          blockBookingMinCustomTicketPrice.currency,
      };
    }
    if (pricingOptions.includes(BLOCK_BOOKING_OPTION)) {
      const childrenBlockBookingPricePerSession = childrenBlockBooking?.amount
        ? childrenBlockBooking.amount / validNumberOfSessions
        : 0;
      const adultBlockBookingPricePerSession = adultBlockBooking?.amount
        ? adultBlockBooking.amount / validNumberOfSessions
        : 0;

      return {
        amount:
          childrenBlockBookingPricePerSession ||
          childrenSingleEvent?.amount ||
          adultBlockBookingPricePerSession ||
          adultSingleEvent?.amount ||
          blockBookingMinCustomTicketPrice.amount ||
          payAsYouGoMinCustomTicketPrice.amount ||
          0,
        currency:
          childrenBlockBooking?.currency ||
          childrenSingleEvent?.currency ||
          adultBlockBooking?.currency ||
          adultSingleEvent?.currency ||
          childrenPayAsYouGo?.currency ||
          payAsYouGoMinCustomTicketPrice.currency ||
          blockBookingMinCustomTicketPrice.currency,
      };
    }

    // Handle case where no valid pricing option is found
    console.warn('No valid pricing option found for LISTING_TYPE_BOOK_CLASS');
  }

  return {
    amount: 0,
    currency:
      childrenSingleEvent?.currency ||
      childrenPayAsYouGo?.currency ||
      childrenBlockBooking?.currency,
  };
};

export const calculateAgeRangeInMonths = (ageRange) => {
  const { ageFrom, ageTo } = ageRange || {};

  if (!ageFrom || !ageTo) {
    return { ageFrom: null, ageTo: null };
  }

  const { number: ageFromNumberString, unit: ageFromUnit } = ageFrom;
  const { number: ageToNumberString, unit: ageToUnit } = ageTo;
  const ageFromNumber = parseInt(ageFromNumberString, 10);
  const ageToNumber = parseInt(ageToNumberString, 10);

  if (Number.isNaN(ageFromNumber) || Number.isNaN(ageToNumber)) {
    return { ageFrom: null, ageTo: null };
  }

  const ageFromInMonths = ageFromUnit === 'years' ? ageFromNumber * 12 : ageFromNumber;
  const ageToInMonths = ageToUnit === 'years' ? ageToNumber * 12 : ageToNumber;

  return {
    ageFrom: ageFromInMonths,
    ageTo: ageToInMonths,
  };
};

// Calendar-aware age in months as of an arbitrary date (e.g. a session date), not "now" —
// existing age calculations elsewhere in the codebase (AttendeeCard, provider-transaction-accepted
// emails) approximate with ms/365.25 and are always relative to today, neither of which fits here.
export const calculateAgeInMonths = (dateOfBirth, asOfDate) => {
  if (!dateOfBirth || !asOfDate) return null;
  return moment(asOfDate).diff(moment(dateOfBirth), 'months');
};

export const getAllAvailableTicketTypes = (data) => {
  const {
    singleEvent = {},
    blockBooking = {},
    payAsYouGo = {},
    pricingOptions,
    listingType,
  } = data || {};

  if (listingType === LISTING_TYPE_SINGLE_EVENT) {
    return singleEvent.ticketTypes || [];
  }

  if (!pricingOptions?.includes(BLOCK_BOOKING_OPTION)) {
    return payAsYouGo.ticketTypes || [];
  }

  return [...new Set([...(blockBooking.ticketTypes || []), ...(payAsYouGo.ticketTypes || [])])];
};

const getTicketPrices = (pricingData) => ({
  childrenPrice: pricingData?.childrenPrice,
  adultPrice: pricingData?.adultPrice,
  siblingPrice: pricingData?.siblingPrice,
});

export const getTicketTypesPrices = (data) => {
  const {
    singleEvent = {},
    blockBooking = {},
    payAsYouGo = {},
    pricingOptions = [],
    listingType,
    numberOfSessions,
  } = data || {};
  if (listingType === LISTING_TYPE_SINGLE_EVENT) {
    const { childrenPrice, adultPrice, siblingPrice } = getTicketPrices(singleEvent);

    return {
      childTicketPrice: childrenPrice ? childrenPrice.amount : null,
      adultTicketPrice: adultPrice ? adultPrice.amount : null,
      siblingTicketPrice: siblingPrice ? siblingPrice.amount : null,
    };
  }

  if (listingType === LISTING_TYPE_BOOK_CLASS) {
    const blockBookingPrices = getTicketPrices(blockBooking);
    const payAsYouGoPrices = getTicketPrices(payAsYouGo);

    if (pricingOptions.includes(BLOCK_BOOKING_OPTION)) {
      const validNumberOfSessions = Math.max(1, numberOfSessions || 1);

      return {
        childTicketPrice: blockBookingPrices.childrenPrice
          ? Math.floor(blockBookingPrices.childrenPrice.amount / validNumberOfSessions)
          : (payAsYouGoPrices.childrenPrice?.amount ?? null),
        adultTicketPrice: blockBookingPrices.adultPrice
          ? Math.floor(blockBookingPrices.adultPrice.amount / validNumberOfSessions)
          : (payAsYouGoPrices.adultPrice?.amount ?? null),
        siblingTicketPrice: blockBookingPrices.siblingPrice
          ? Math.floor(blockBookingPrices.siblingPrice.amount / validNumberOfSessions)
          : (payAsYouGoPrices.siblingPrice?.amount ?? null),
      };
    }

    return {
      childTicketPrice: payAsYouGoPrices.childrenPrice
        ? payAsYouGoPrices.childrenPrice.amount
        : null,
      adultTicketPrice: payAsYouGoPrices.adultPrice ? payAsYouGoPrices.adultPrice.amount : null,
      siblingTicketPrice: payAsYouGoPrices.siblingPrice
        ? payAsYouGoPrices.siblingPrice.amount
        : null,
    };
  }

  return {
    childTicketPrice: null,
    adultTicketPrice: null,
    siblingTicketPrice: null,
  };
};
